import api from "@/api/url";
import { sortList, calculateTotalPages } from "@/utils/helpers";

export default {
    namespaced: true,

    state: () => {
        return {
            projects: [],
            recentProjects: [],
            pageData: {
                currentPage: 1,
                totalPages: 0,
                pageSize: 50,
                total: 0,
            },
            refreshList: true,
            trackers: [],
            timeLogs: {
                list: [],
                pageData: {
                    currentPage: 1,
                    totalPages: 0,
                    pageSize: 50,
                    total: 0,
                },
            },
        };
    },

    getters: {
        projects: (state) => state.projects,
        pageData: (state) => state.pageData,
        currentProjectTask: state => state.currentProjectTask,
        refreshList: state => state.refreshList,
        timeLogs: state => state.timeLogs,
    },

    mutations: {
        set(state, [key, value]) {
            state[key] = value;
        },
    },
    
    actions: {
        setRefreshList({ commit }, bool) {
            commit("set", ["refreshList", bool]);
        },

        addProjectNoRequest({ commit, state }, payload) {
            const pageData = {...state.pageData};

            commit("set", ["projects", [ payload, ...state.projects ]]);
            commit("set", ["pageData", {
                ...pageData,
                total: pageData?.total + 1,
                totalPages: calculateTotalPages(pageData?.total + 1, pageData.pageSize),
            }]);    
        },
        
        addProject({ state, commit }, { payload, external = false }) {   
            // optimistic updates
            const list = state.projects;
            const pageData = state.pageData;
        
            return api.project.create(payload)
                .then((resp) => {
                    const data = resp?.data?.data;
                    
                    if (!data) {
                        throw new Error("Invalid response format");
                    }

                    commit("set", ["projects", [ { ...payload, ...data }, ...list ]]);
                    commit("set", ["pageData", {
                        ...pageData,
                        total: pageData?.total + 1,
                        totalPages: Math.ceil((pageData?.total + 1) / pageData.pageSize),
                    }]);     

                    if (external) return;                        
                    return data;
                })
                .catch((error) => {
                    commit("set", ["projects", list]);
                    commit("set", ["pageData", {
                        ...pageData,
                        total: pageData?.total - 1,
                        totalPages: Math.ceil((pageData?.total - 1) / pageData.pageSize),
                    }]);   

                    throw error; // Re-throw for component handling
                });
        },
        
        fetchProjects({ commit, state }, { params, refresh, config = {}, external = false }) {
            if (!refresh && state.projects.length) return;
            
            return api.project.getAll(params, config)
                .then((resp) => {                    
                    if (external) {
                        return resp?.data?.data;
                    } 
                    
                    if (config?.responseType) {
                        const data = resp?.data;
                        return data;
                    }
                    
                    const data = resp?.data?.data;
                    commit("set", ["projects", data?.data?.data]);
                    commit("set", ["pageData", {
                        ...state.pageData,
                        ... data.pageDetails,
                    }]);
                    commit("set", ["setRefreshList", false]);
                });
        },

        getProject(_, id) {
            return api.project.getOne(id)
                .then((resp) => {
                    const data = resp?.data?.data;                    
                    return data;
                });
        }, 
 
        updateProject({ commit, state }, { id, payload, externalPayload = {} }) {                        
            return api.project.updateOne(id, payload)
                .then((resp) => {
                    const data = resp?.data?.data;
                    const projects = state.projects;
                    const updatedData = { ...externalPayload, ...data };
                    
                    const updatedProjects = projects.map(c => {
                        if (c?._id === payload?._id) {
                            return {
                                ...c,
                                ...updatedData,
                            };
                        } else {
                            return c;
                        }
                    });
        
                    commit("set", ["projects", updatedProjects]);   
                    return {...updatedData };   // did this to be able to return the ratecard object without having to make a request to the get single project endpoint    
                });
        },

        deleteProjects({ state, commit }, ids) {
            return api.project.delete({ ids: ids })
                .then((resp) => {
                    // optimistic updates
                    const projects = state.projects;
                    const updatedProjects = projects.filter(c => !ids.includes(c?._id));

                    commit("set", ["projects", updatedProjects]);
                    commit("set", ["pageData", {
                        ...state.pageData,
                        total: state.pageData.total - ids.length,
                        totalPages: Math.ceil((state.pageData.total - ids.length) / state.pageData.pageSize),
                    }]);
            
                    return resp;
                }).catch(error => {
                    commit("set", ["projects", projects]);
                    commit("set", ["pageData", {
                        ...state.pageData,
                        total: state.pageData.total + ids.length,
                        totalPages: Math.ceil((state.pageData.total + ids.length) / state.pageData.pageSize),
                    }]);   
                    throw error; // Re-throw for component handling
                });
        },

        uploadList(_, { formData, progressCB }) {            
            return api.project.upload(formData, { headers: { "Content-Type": "multipart/form-data" }, onUploadProgress: progressCB })
                .then((resp) => {});
        },

        sortProjects({ commit, state }, sortVal) {
            const copiedArr = state.projects.slice();
            
            sortList(sortVal, copiedArr, "title");
                        
            commit("set", ["projects", copiedArr]);
        }, 

        fetchRecentProjects({ commit, state }) {
            if (state.recentProjects.length) {
                return state.recentProjects;
            }

            return api().project.getAll(params)
                .then((resp) => {
                    const data = resp?.data?.data;

                    commit("set", ["recentProjects", data.data]);
                    return data?.data;
                });
        },

        uploadMutlipleProjects(_, { formData, progressCB }) {            
            return api.project.upload(formData, { headers: { "Content-Type": "multipart/form-data" }, onUploadProgress: progressCB })
                .then((resp) => {});
        },

        archiveProject({ state, commit }, id) {            
            return api.project.archive(id)
                .then((resp) => {
                    const data = resp.data.data;
                    const projects = state.projects;

                    const updatedProjects = projects.map(c => {
                        if (c?._id === data?._id) {
                            return {
                                ...c,
                                ...data,
                            };
                        } else {
                            return c;
                        }
                    });

                    commit("set", ["projects", updatedProjects]); 
                    commit("set", ["pageData", {
                        ...state.pageData,
                        total: state.pageData.total - 1,
                        totalPages: Math.ceil((state.pageData.total - 1) / 50),
                    }]);
                });
        },

        updateProjectSubs(_, { id, payload }) {
            return api.project.updateSub(id, payload)
                .then((resp) => {});
        },

        removeItemFromStore({ commit }, { storageKey, stateKey, value }) {
            localStorage.removeItem(storageKey);
            commit("set", [`${stateKey}`, value]);
        },

        duplicateProject(_, id) {      
            return api.project.duplicate(id)
                .then((resp) => {
                    const data = resp?.data?.data;
                    return data;
                });
        },

        scheduleProject({ state, commit }, { payload, id }) {            
            return api.project.schedule(id, payload)
                .then((resp) => {
                    const data = resp.data.data;
                    const projects = state.projects;

                    const updatedProjects = projects.map(c => {
                        if (c?._id === data?._id) {
                            return {
                                ...c,
                                ...data,
                            };
                        } else {
                            return c;
                        }
                    });

                    commit("set", ["projects", updatedProjects]); 
                });
        },

        startProject({ state, commit }, { id, payload }) { 
            return api.project.start(id, payload)
                .then((resp) => {
                    const data = resp.data.data;
                    const projects = state.projects;

                    const updatedProjects = projects.map(c => {
                        if (c?._id === data?._id) {
                            return {
                                ...c,
                                ...data,
                            };
                        } else {
                            return c;
                        }
                    });

                    commit("set", ["projects", updatedProjects]); 
                    return data;
                });
        },

        // PROJECT TRACKERS
        getTrackers({ commit }, { projectId }) {
            return api.project.getTrackers(projectId)
                .then((resp) => {
                    commit("set", ["trackers", resp?.data.data]);
                    return resp?.data.data;      
                });
        },

        setNewTracker({ state, commit }, payload) {   
            commit("set", ["trackers", [ payload, ...state.trackers ]]);  
        },

        // PROJECT LOGS
        fetchLogs({ commit, state }, { projectId, params, config, download = false }) {
            return api.project.fetchLogs(projectId, params, config)
                .then((resp) => {
                    if (download) {
                        return resp.data;
                    }
                    const data = resp?.data?.data;  
                    
                    commit("set", ["timeLogs", {
                        ...state.timeLogs,
                        list: data.data,
                        pageData: data.pageDetails,
                    }]);

                    return data.data;      
                });
        },

        toggleLog(_, { projectId, payload }) {
            return api.project.toggleLog(projectId, payload)
                .then((resp) => {
                    const data = resp?.data?.data;
                    return data;      
                });
        },

        // PROJECT LINKS
        fetchLinks(_, projectId) {
            return api.project.fetchLinks(projectId)
                .then((resp) => {                    
                    return resp?.data?.data;  
                });
        },

        manageLinks(_, { projectId, payload, params }) {
            return api.project.manageLinks(projectId, params, payload)
                .then((resp) => {
                    const data = resp?.data?.data;                    
                    return data;      
                });
        },
        
        // ------------- INVOICES ------------------
        fetchProjectInvoices(_, { id, params }) {
            return api.project.getInvoices(id, params)
                .then((resp) => resp.data.data);
        }, 

        // ------------ NOTES ---------------
        getNotes(_, { id, params }) {
            return api.project.getNotes(id, params)
                .then((resp) => {
                    return resp.data.data;
                });
        },

        addNewNote(_, { id, value }) {
            return api.project.createNote(id, { value })
                .then((resp) => {
                    return resp.data.data;
                });
        },

        updateNote(_, payload) {
            return api.project.editNote(payload.projectId, payload)
                .then((resp) => {
                    return resp.data.data;
                });
        },
        
        deleteNote(_, payload) {
            return api.project.deleteNote(payload.projectId, payload)
                .then((resp) => {
                    return resp.data.data;
                });
        },

        // ------------- TASKS ------------------
        addTask(_, payload) {
            return api.task.addTask(payload)
                .then((resp) => {
                    return resp.data.data;
                });
        },

        editTask(_, payload) {
            return api.task.editTask(payload?._id, payload)
                .then((resp) => {
                    return resp.data?.data;
                });
        },

        deleteTask(_, id) {
            return api.task.deleteTask(id)
                .then((resp) => {});
        },

        fetchTasks(_, { params }) {
            return api.task.getTasks(params)
                .then((resp) => {
                    const data = resp?.data?.data;
                    return data;
                });
        },

        duplicateTask({ commit }, { task, payload }) {
            return api.task.duplicateTask(payload)
                .then((resp) => {
                    const data = resp.data.data;  
                    return { ...data, ...task };
                });
        },
    },
};